import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Receipt, Users, IdentificationCard, Plus, MagnifyingGlass, Trash, CircleNotch, X,
  CheckCircle, UploadSimple, PencilSimple, Buildings, Stamp, Signature, Image as ImageIcon,
  Calculator, FloppyDisk, DownloadSimple, ArrowsClockwise, FileArrowUp, FileText, SealCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ITREngine from "./taxos/ITREngine";
import AuditEngine from "./taxos/AuditEngine";
import AutoFillIntake from "./taxos/AutoFillIntake";
import IndividualFinancials from "./taxos/IndividualFinancials";
import CompanyFinancials from "./taxos/CompanyFinancials";
import BulkRoll from "./taxos/BulkRoll";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
let _rid = 0;
const rid = () => `tx${++_rid}`;
const TABS = [
  { id: "clients", label: "Clients", icon: Users },
  { id: "financials", label: "Financials", icon: Calculator },
  { id: "intake", label: "Doc Intake", icon: FileArrowUp },
  { id: "ca", label: "CA Profiles", icon: IdentificationCard },
];

export default function TaxOS() {
  const [tab, setTab] = useState("clients");
  const [meta, setMeta] = useState({ letterhead_styles: [], business_types: [], financial_years: [] });
  const [itrMeta, setItrMeta] = useState(null);
  const [auditMeta, setAuditMeta] = useState(null);
  const [showBulkRoll, setShowBulkRoll] = useState(false);

  // clients
  const [clients, setClients] = useState([]);
  const [count, setCount] = useState(0);
  const [q, setQ] = useState("");
  const [fyFilter, setFyFilter] = useState("");
  const [showClient, setShowClient] = useState(false);
  const [editClient, setEditClient] = useState(null);

  // CA profiles
  const [profiles, setProfiles] = useState([]);
  const [showCA, setShowCA] = useState(false);
  const [editCA, setEditCA] = useState(null);
  const assetRef = useRef({});

  const loadMeta = useCallback(async () => { try { const r = await api.get("/tax/meta"); setMeta(r.data); } catch (e) { console.error(e); } }, []);
  const loadEngineMeta = useCallback(async () => {
    try { const [a, b] = await Promise.all([api.get("/tax/itr/meta"), api.get("/tax/audit/meta")]); setItrMeta(a.data); setAuditMeta(b.data); } catch (e) { console.error(e); }
  }, []);
  const loadClients = useCallback(async () => {
    try {
      const r = await api.get("/tax/clients", { params: { q: q || undefined, fy: fyFilter || undefined } });
      setClients(r.data.clients); setCount(r.data.total);
    } catch (e) { console.error(e); }
  }, [q, fyFilter]);
  const loadProfiles = useCallback(async () => { try { const r = await api.get("/tax/ca-profiles"); setProfiles(r.data); } catch (e) { console.error(e); } }, []);

  useEffect(() => { loadMeta(); loadProfiles(); loadEngineMeta(); }, [loadMeta, loadProfiles, loadEngineMeta]);
  useEffect(() => { if (tab === "clients") loadClients(); }, [tab, loadClients]);

  const saveClient = async (data) => {
    try { await api.post("/tax/clients", data); toast.success("Client saved"); setShowClient(false); setEditClient(null); loadClients(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const delClient = async (id) => { if (!window.confirm("Delete this client?")) return; try { await api.delete(`/tax/clients/${id}`); loadClients(); } catch (e) { console.error(e); } };

  const saveCA = async (data) => {
    try { const r = await api.post("/tax/ca-profiles", data); toast.success("CA profile saved"); setEditCA(r.data); loadProfiles(); return r.data; }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const activateCA = async (id) => { try { await api.post(`/tax/ca-profiles/${id}/activate`); loadProfiles(); toast.success("Active CA set"); } catch (e) { console.error(e); } };
  const delCA = async (id) => { if (!window.confirm("Delete this CA profile?")) return; try { await api.delete(`/tax/ca-profiles/${id}`); loadProfiles(); } catch (e) { console.error(e); } };
  const uploadAsset = async (caId, kind, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/tax/ca-profiles/${caId}/asset/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind} uploaded`); const fresh = await api.get("/tax/ca-profiles"); setProfiles(fresh.data);
      setEditCA((c) => c && c.ca_id === caId ? { ...c, [kind]: true, _v: Date.now() } : c);
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <div className="h-16 flex items-center -mt-4 mb-2" />
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#FFF4F0] flex items-center justify-center"><Receipt size={24} weight="duotone" className="text-[#F29A86]" /></div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">ZEYRODHA.TaxOS</h1>
          <p className="text-[#594F4D] text-sm">CA practice — manage CA profiles & clients, financials, ITR & 3CB-3CD</p>
        </div>
      </div>

      <div className="flex gap-2 mt-6 border-b border-[#2A2625]/10">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tax-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#F29A86] text-[#2A2625]" : "border-transparent text-[#8C8381] hover:text-[#594F4D]"}`}>
            <t.icon size={17} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "clients" && (
        <div className="mt-6" data-testid="tax-clients">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8381]" />
              <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by firm name or PAN…"
                className="w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
            </div>
            <select data-testid="client-fy-filter" value={fyFilter} onChange={(e) => setFyFilter(e.target.value)} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
              <option value="">All F.Y.</option>
              {meta.financial_years.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <button data-testid="bulk-roll-btn" onClick={() => setShowBulkRoll(true)} className="flex items-center gap-2 border border-[#2A2625]/15 hover:bg-[#2A2625]/5 text-[#2A2625] font-medium rounded-full px-4 py-2.5 transition-colors"><ArrowsClockwise size={16} weight="bold" /> Bulk roll</button>
            <button data-testid="add-client-btn" onClick={() => { setEditClient(null); setShowClient(true); }} className="flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-5 py-2.5 transition-colors"><Plus size={16} weight="bold" /> Add client</button>
          </div>
          <p className="text-xs text-[#8C8381] mb-3">{count} / 500 clients</p>
          <div className="bg-white border border-[#2A2625]/10 rounded-2xl overflow-hidden">
            {clients.length === 0 ? <p className="p-6 text-sm text-[#8C8381]">No clients yet. Add your first client.</p> : (
              <table className="w-full text-sm" data-testid="clients-table">
                <thead><tr className="text-left text-[#8C8381] text-xs uppercase tracking-wide bg-[#FCFAF8]">
                  <th className="px-5 py-3 font-medium">Firm / Name</th><th className="px-4 py-3 font-medium">PAN</th><th className="px-4 py-3 font-medium">Type</th><th className="px-4 py-3 font-medium">F.Y. saved</th><th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.client_id} className="border-t border-[#2A2625]/8" data-testid={`client-row-${c.client_id}`}>
                      <td className="px-5 py-3 font-medium">{c.firm_name}</td>
                      <td className="px-4 py-3 font-mono text-xs">{c.pan || "—"}</td>
                      <td className="px-4 py-3">{c.business_type}</td>
                      <td className="px-4 py-3">{(c.fys || []).length ? (c.fys || []).map((f) => <span key={f} className="inline-block text-xs bg-[#FFF4F0] rounded-full px-2 py-0.5 mr-1">{f}</span>) : <span className="text-[#8C8381]">—</span>}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => { setEditClient(c); setShowClient(true); }} className="text-[#8C8381] hover:text-[#2A2625] mr-3"><PencilSimple size={16} /></button>
                        <button data-testid={`del-client-${c.client_id}`} onClick={() => delClient(c.client_id)} className="text-[#8C8381] hover:text-[#E09494]"><Trash size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === "ca" && (
        <div className="mt-6" data-testid="tax-ca">
          <button data-testid="add-ca-btn" onClick={() => { setEditCA(null); setShowCA(true); }} className="flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-5 py-2.5 mb-4 transition-colors"><Plus size={16} weight="bold" /> Add CA profile</button>
          <div className="grid md:grid-cols-2 gap-4">
            {profiles.length === 0 && <p className="text-sm text-[#8C8381]">No CA profiles yet.</p>}
            {profiles.map((p) => (
              <div key={p.ca_id} className={`bg-white border rounded-2xl p-5 ${p.is_active ? "border-[#F29A86]" : "border-[#2A2625]/10"}`} data-testid={`ca-card-${p.ca_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-display font-semibold">{p.name} {p.is_active && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#A3C2A4]/30 text-[#3c663d]">ACTIVE</span>}</p>
                    <p className="text-xs text-[#8C8381] mt-0.5">{p.designation} · FRN {p.frn || "—"} · M.No {p.membership_no || "—"}</p>
                    <p className="text-xs text-[#8C8381]">Letterhead: {p.letterhead_style}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditCA(p); setShowCA(true); }} className="text-[#8C8381] hover:text-[#2A2625]"><PencilSimple size={16} /></button>
                    <button onClick={() => delCA(p.ca_id)} className="text-[#8C8381] hover:text-[#E09494]"><Trash size={16} /></button>
                  </div>
                </div>
                <div className="flex gap-3 mt-3">
                  {["logo", "seal", "sign"].map((k) => (
                    <span key={k} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg ${p[k] ? "bg-[#A3C2A4]/25 text-[#3c663d]" : "bg-[#2A2625]/8 text-[#8C8381]"}`}>
                      {k === "logo" ? <ImageIcon size={12} /> : k === "seal" ? <Stamp size={12} /> : <Signature size={12} />} {k}{p[k] ? " ✓" : ""}
                    </span>
                  ))}
                </div>
                {!p.is_active && <button data-testid={`activate-ca-${p.ca_id}`} onClick={() => activateCA(p.ca_id)} className="mt-3 text-sm border border-[#2A2625]/15 rounded-full px-4 py-1.5 hover:bg-[#2A2625]/5 transition-colors">Set active</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "financials" && <Financials meta={meta} clients={clients} reloadClients={loadClients} />}
      {tab === "intake" && <DocIntake clients={clients} reloadClients={loadClients} />}

      {showClient && <ClientModal meta={meta} initial={editClient} onClose={() => { setShowClient(false); setEditClient(null); }} onSave={saveClient} />}
      {showBulkRoll && <BulkRoll financialYears={meta.financial_years} onClose={() => { setShowBulkRoll(false); loadClients(); }} />}
      {showCA && <CAModal meta={meta} initial={editCA} onClose={() => { setShowCA(false); setEditCA(null); }} onSave={saveCA} onUpload={uploadAsset} assetRef={assetRef} />}
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs text-[#8C8381]">{label}</label><div className="mt-1">{children}</div></div>;
}
const inputCls = "w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]";

function ClientModal({ meta, initial, onClose, onSave }) {
  const [f, setF] = useState(initial || { firm_name: "", pan: "", business_type: "Individual", nature_of_business: "", mobile: "", email: "", address: "", fys: [] });
  const toggleFy = (fy) => setF((p) => ({ ...p, fys: (p.fys || []).includes(fy) ? p.fys.filter((x) => x !== fy) : [...(p.fys || []), fy] }));
  return (
    <Modal title={initial ? "Edit client" : "Add client"} onClose={onClose} testid="client-modal">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Firm / Proprietor name *"><input data-testid="client-firm-name" value={f.firm_name} onChange={(e) => setF({ ...f, firm_name: e.target.value })} className={inputCls} /></Field>
        <Field label="PAN"><input data-testid="client-pan" value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} className={inputCls} /></Field>
        <Field label="Business type"><select value={f.business_type} onChange={(e) => setF({ ...f, business_type: e.target.value })} className={inputCls}>{meta.business_types.map((b) => <option key={b}>{b}</option>)}</select></Field>
        <Field label="Nature of business"><input value={f.nature_of_business} onChange={(e) => setF({ ...f, nature_of_business: e.target.value })} className={inputCls} /></Field>
        <Field label="Mobile"><input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} className={inputCls} /></Field>
        <Field label="Email"><input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} /></Field>
        <div className="sm:col-span-2"><Field label="Address"><input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} /></Field></div>
        <div className="sm:col-span-2"><Field label="Financial years on record">
          <div className="flex gap-2 flex-wrap">{meta.financial_years.map((fy) => (
            <button key={fy} type="button" data-testid={`client-fy-${fy}`} onClick={() => toggleFy(fy)} className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${(f.fys || []).includes(fy) ? "bg-[#FFB8A5] border-[#FFB8A5] text-[#2A2625]" : "border-[#2A2625]/15 text-[#594F4D]"}`}>{fy}</button>
          ))}</div></Field></div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="text-sm border border-[#2A2625]/15 rounded-full px-4 py-2.5">Cancel</button>
        <button data-testid="save-client-btn" onClick={() => f.firm_name.trim() ? onSave(f) : toast.error("Firm name required")} className="text-sm bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-5 py-2.5 transition-colors">Save client</button>
      </div>
    </Modal>
  );
}

function CAModal({ meta, initial, onClose, onSave, onUpload, assetRef }) {
  const [f, setF] = useState(initial || { name: "", frn: "", membership_no: "", pan: "", address: "", signatory: "", designation: "Partner", letterhead_style: "Classic Navy", udin: "" });
  const caId = f.ca_id;
  const save = async () => { if (!f.name.trim()) { toast.error("Name required"); return; } const saved = await onSave(f); if (saved) setF(saved); };
  return (
    <Modal title={initial ? "Edit CA profile" : "Add CA profile"} onClose={onClose} testid="ca-modal">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="CA / Firm name *"><input data-testid="ca-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></Field>
        <Field label="FRN"><input data-testid="ca-frn" value={f.frn} onChange={(e) => setF({ ...f, frn: e.target.value })} className={inputCls} /></Field>
        <Field label="Membership No."><input value={f.membership_no} onChange={(e) => setF({ ...f, membership_no: e.target.value })} className={inputCls} /></Field>
        <Field label="PAN"><input value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} className={inputCls} /></Field>
        <Field label="Signatory name"><input value={f.signatory} onChange={(e) => setF({ ...f, signatory: e.target.value })} className={inputCls} /></Field>
        <Field label="Designation"><select value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} className={inputCls}>{["Partner", "Proprietor", "Chartered Accountant"].map((d) => <option key={d}>{d}</option>)}</select></Field>
        <Field label="Default UDIN (optional)"><input data-testid="ca-udin" value={f.udin || ""} onChange={(e) => setF({ ...f, udin: e.target.value })} className={inputCls} placeholder="Reused on signed PDFs" /></Field>
        <div className="sm:col-span-2"><Field label="Address"><input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} /></Field></div>
        <div className="sm:col-span-2"><Field label="Letterhead style"><select data-testid="ca-letterhead" value={f.letterhead_style} onChange={(e) => setF({ ...f, letterhead_style: e.target.value })} className={inputCls}>{meta.letterhead_styles.map((s) => <option key={s}>{s}</option>)}</select></Field></div>
      </div>
      {!caId ? <p className="text-xs text-[#8C8381] mt-4">Save the profile first, then upload seal / signature / logo.</p> : (
        <div className="mt-4 border-t border-[#2A2625]/8 pt-4">
          <p className="text-xs text-[#8C8381] mb-2">Branding assets</p>
          <div className="flex gap-3 flex-wrap">
            {["logo", "seal", "sign"].map((k) => (
              <div key={k} className="flex items-center gap-2">
                <input type="file" accept="image/*" className="hidden" ref={(el) => (assetRef.current[k] = el)} onChange={(e) => onUpload(caId, k, e.target.files?.[0])} />
                <button data-testid={`upload-ca-${k}`} onClick={() => assetRef.current[k]?.click()} className="flex items-center gap-1.5 text-sm border border-dashed border-[#2A2625]/25 rounded-xl px-3 py-2 hover:bg-[#2A2625]/5 transition-colors"><UploadSimple size={14} /> {k}</button>
                {f[k] && <img alt={k} src={`${BACKEND}/api/tax/ca-assets/${caId}/${k}?v=${f._v || 0}`} className="w-9 h-9 rounded-lg object-contain border border-[#2A2625]/10 bg-white" onError={(e) => (e.currentTarget.style.display = "none")} />}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="text-sm border border-[#2A2625]/15 rounded-full px-4 py-2.5">Close</button>
        <button data-testid="save-ca-btn" onClick={save} className="text-sm bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full px-5 py-2.5 transition-colors">Save profile</button>
      </div>
    </Modal>
  );
}

function EditRows({ title, rows, onChange }) {
  const upd = (i, field, val) => { const r = [...rows]; r[i] = { ...r[i], [field]: field === "amount" ? val : val }; onChange(r); };
  const add = () => onChange([...rows, { _uid: rid(), label: "New item", amount: 0 }]);
  const del = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  return (
    <div className="bg-white border border-[#2A2625]/10 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2"><p className="text-sm font-semibold">{title}</p><span className="text-xs text-[#8C8381]">₹{total.toLocaleString()}</span></div>
      {rows.map((r, i) => (
        <div key={r._uid ?? `row-${i}`} className="flex gap-2 mb-1.5">
          <input value={r.label} onChange={(e) => upd(i, "label", e.target.value)} className="flex-1 bg-[#FCFAF8] border border-[#2A2625]/12 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-[#FFB8A5]" />
          <input type="number" value={r.amount} onChange={(e) => upd(i, "amount", e.target.value)} className="w-28 text-right bg-[#FCFAF8] border border-[#2A2625]/12 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-[#FFB8A5]" />
          <button onClick={() => del(i)} className="text-[#8C8381] hover:text-[#E09494]"><Trash size={13} /></button>
        </div>
      ))}
      <button onClick={add} className="text-xs text-[#F29A86] flex items-center gap-1 mt-1"><Plus size={12} /> Add row</button>
    </div>
  );
}

function Financials({ meta, clients, reloadClients }) {
  const [clientId, setClientId] = useState("");
  const [fy, setFy] = useState("2025-26");
  const [data, setData] = useState(null);
  const [der, setDer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [growth, setGrowth] = useState(10);
  const [dir, setDir] = useState("forward");
  const [targetFy, setTargetFy] = useState("2026-27");

  const load = useCallback(async () => {
    if (!clientId) return;
    try { const r = await api.get(`/tax/statements/${clientId}/${fy}`); setData(r.data.data); setDer(r.data.derived || null); }
    catch { toast.error("Load failed"); }
  }, [clientId, fy]);
  useEffect(() => { load(); }, [load]);
  const isIndiv = data?.format === "individual" || data?.format === "partnership";
  const isCompany = data?.format === "company";
  // live-derive for the individual format (Cash-in-Hand plug, GP/NP/capital) on every edit
  useEffect(() => {
    if ((!isIndiv && !isCompany) || !data) return;
    const t = setTimeout(async () => {
      try { const r = await api.post("/tax/statements/derive", { client_id: clientId, fy, data }); setDer(r.data.derived); } catch (e) { console.error(e); }
    }, 350);
    return () => clearTimeout(t);
  }, [data, isIndiv, isCompany, clientId, fy]);

  const setSec = (sec, key, rows) => setData((d) => ({ ...d, [sec]: { ...d[sec], [key]: rows } }));
  const save = async () => {
    if (!clientId) { toast.error("Select a client"); return; }
    setBusy(true);
    try { const r = await api.post("/tax/statements", { client_id: clientId, fy, data }); setDer(r.data.derived); toast.success("Statement saved"); reloadClients(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const roll = async () => {
    if (!clientId) { toast.error("Select a client"); return; }
    setBusy(true);
    try {
      const r = await api.post("/tax/statements/roll", { client_id: clientId, source_fy: fy, target_fy: targetFy, growth_pct: Number(growth), direction: dir });
      toast.success(`Rolled ${dir} to ${targetFy} (×${r.data.factor})`); setFy(targetFy); setData(r.data.data); setDer(r.data.derived); reloadClients();
    } catch (e) { toast.error(e?.response?.data?.detail || "Roll failed"); } finally { setBusy(false); }
  };
  const exportFile = (fmt) => {
    const url = `${BACKEND}/api/tax/statements/${clientId}/${fy}/export?format=${fmt}`;
    fetch(url, { credentials: "include" }).then((res) => res.ok ? res.blob() : Promise.reject()).then((b) => {
      const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `statement_${fy}.${fmt === "excel" ? "xlsx" : "pdf"}`; a.click();
    }).catch(() => toast.error("Export failed — save the statement first"));
  };

  return (
    <div className="mt-6" data-testid="tax-financials">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="text-xs text-[#8C8381]">Client</label>
          <select data-testid="fin-client" value={clientId} onChange={(e) => setClientId(e.target.value)} className="mt-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none w-56"><option value="">Select client…</option>{clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.firm_name}</option>)}</select>
        </div>
        <div><label className="text-xs text-[#8C8381]">F.Y.</label>
          <select data-testid="fin-fy" value={fy} onChange={(e) => setFy(e.target.value)} className="mt-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none">{meta.financial_years.map((f) => <option key={f}>{f}</option>)}</select>
        </div>
        {!isIndiv && !isCompany && <>
          <button data-testid="fin-save" onClick={save} disabled={busy || !data} className="flex items-center gap-2 bg-[#2A2625] text-white font-medium rounded-full px-5 py-2.5 text-sm">{busy ? <CircleNotch size={15} className="animate-spin" /> : <FloppyDisk size={15} />} Save</button>
          <button data-testid="fin-export-pdf" onClick={() => exportFile("pdf")} disabled={!clientId} className="flex items-center gap-2 border border-[#2A2625]/15 rounded-full px-4 py-2.5 text-sm hover:bg-[#2A2625]/5"><DownloadSimple size={15} /> PDF</button>
          <button data-testid="fin-export-excel" onClick={() => exportFile("excel")} disabled={!clientId} className="flex items-center gap-2 bg-[#A3C2A4]/25 text-[#3c663d] rounded-full px-4 py-2.5 text-sm"><DownloadSimple size={15} /> Excel</button>
        </>}
      </div>

      {clientId && isIndiv && <IndividualFinancials clientId={clientId} fy={fy} data={data} setData={setData} der={der} meta={meta} reloadClients={reloadClients} entity={data?.format} />}
      {clientId && isCompany && <CompanyFinancials clientId={clientId} fy={fy} data={data} setData={setData} der={der} meta={meta} reloadClients={reloadClients} />}

      {!isIndiv && !isCompany && der && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="fin-derived">
          {[["Gross Profit", der.gross_profit], ["Net Profit", der.net_profit], ["Assets", der.assets_total], ["BS Difference", der.difference]].map(([l, v]) => (
            <div key={l} className="bg-[#FFF4F0] rounded-xl p-3"><p className="text-lg font-display font-semibold">₹{Number(v).toLocaleString()}</p><p className="text-xs text-[#8C8381]">{l}</p></div>
          ))}
        </div>
      )}

      {clientId && !isIndiv && !isCompany && (
        <div className="bg-white border border-[#2A2625]/10 rounded-xl p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="fin-roll">
          <span className="text-sm font-medium">Roll</span>
          <select value={dir} onChange={(e) => setDir(e.target.value)} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm"><option value="forward">Forward</option><option value="backward">Backward</option></select>
          <div><label className="text-xs text-[#8C8381]">Growth %</label><input data-testid="fin-growth" type="number" value={growth} onChange={(e) => setGrowth(e.target.value)} className="mt-1 w-24 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm" /></div>
          <div><label className="text-xs text-[#8C8381]">Target F.Y.</label><select data-testid="fin-target-fy" value={targetFy} onChange={(e) => setTargetFy(e.target.value)} className="mt-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm">{meta.financial_years.map((f) => <option key={f}>{f}</option>)}</select></div>
          <button data-testid="fin-roll-btn" onClick={roll} disabled={busy} className="flex items-center gap-2 bg-[#FFB8A5] text-[#2A2625] font-medium rounded-full px-4 py-2 text-sm"><ArrowsClockwise size={15} /> Apply</button>
        </div>
      )}

      {!clientId ? <p className="text-sm text-[#8C8381]">Select a client and F.Y. to edit financials.</p> : !isIndiv && !isCompany && data && (
        <div className="grid gap-4">
          <p className="text-xs font-semibold text-[#8C8381] uppercase tracking-wide">Trading Account</p>
          <div className="grid md:grid-cols-2 gap-3">
            <EditRows title="Debits" rows={data.trading.debits} onChange={(r) => setSec("trading", "debits", r)} />
            <EditRows title="Credits (Sale, Closing Stock)" rows={data.trading.credits} onChange={(r) => setSec("trading", "credits", r)} />
          </div>
          <p className="text-xs font-semibold text-[#8C8381] uppercase tracking-wide mt-2">Profit & Loss</p>
          <div className="grid md:grid-cols-2 gap-3">
            <EditRows title="Expenses (Dr)" rows={data.pl.debits} onChange={(r) => setSec("pl", "debits", r)} />
            <EditRows title="Other Incomes" rows={data.pl.other_incomes} onChange={(r) => setSec("pl", "other_incomes", r)} />
          </div>
          <p className="text-xs font-semibold text-[#8C8381] uppercase tracking-wide mt-2">Balance Sheet</p>
          <div className="grid md:grid-cols-2 gap-3">
            <EditRows title="Liabilities" rows={data.bs.liabilities} onChange={(r) => setSec("bs", "liabilities", r)} />
            <EditRows title="Assets" rows={data.bs.assets} onChange={(r) => setSec("bs", "assets", r)} />
          </div>
          <div className="bg-white border border-[#2A2625]/10 rounded-xl p-4">
            <p className="text-sm font-semibold mb-2">Notes to Accounts / Auditor's Annexure (prints on PDF)</p>
            <textarea data-testid="fin-notes" value={data.notes || ""} onChange={(e) => setData({ ...data, notes: e.target.value })} rows={4} placeholder="One note per line…" className="w-full bg-[#FCFAF8] border border-[#2A2625]/12 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#FFB8A5]" />
          </div>
        </div>
      )}
    </div>
  );
}

function DocIntake({ clients }) {
  const KINDS = [
    { id: "bank", label: "Bank statements (up to 7)", max: 7 },
    { id: "loan", label: "Loan statements (up to 7)", max: 7 },
    { id: "ais", label: "AIS", max: 3 },
    { id: "tis", label: "TIS", max: 3 },
    { id: "26as", label: "Form 26AS", max: 3 },
    { id: "personal", label: "Personal docs (PAN/Aadhaar/bank)", max: 10 },
  ];
  const [kind, setKind] = useState("bank");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const ref = useRef(null);
  const run = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true); setOut(null);
    try {
      const fd = new FormData(); files.forEach((f) => fd.append("files", f)); fd.append("kind", kind);
      const r = await api.post("/tax/extract-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOut(r.data); toast.success(`Extracted ${r.data.count} document(s)`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Extraction failed (check LLM budget)"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="mt-6" data-testid="tax-intake">
      <AutoFillIntake clients={clients} />
      <p className="text-sm text-[#594F4D] mb-4">Or extract raw figures from any document/image — a high-accuracy AI OCR reads bank & loan statements, AIS/TIS/26AS and personal docs.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {KINDS.map((k) => (
          <button key={k.id} data-testid={`intake-kind-${k.id}`} onClick={() => setKind(k.id)} className={`text-sm px-3 py-2 rounded-xl border transition-colors ${kind === k.id ? "bg-[#FFB8A5] border-[#FFB8A5] text-[#2A2625]" : "border-[#2A2625]/15 text-[#594F4D] hover:bg-[#2A2625]/5"}`}>{k.label}</button>
        ))}
      </div>
      <input ref={ref} type="file" multiple className="hidden" onChange={run} accept="image/*,.pdf,.csv,.xlsx,.txt,.json,.docx" />
      <button data-testid="intake-upload-btn" onClick={() => ref.current?.click()} disabled={busy} className="flex items-center gap-2 bg-[#2A2625] text-white font-medium rounded-full px-5 py-2.5 text-sm">
        {busy ? <CircleNotch size={16} className="animate-spin" /> : <FileArrowUp size={16} weight="bold" />} Upload & extract ({kind})
      </button>
      {out && (
        <div className="mt-5 space-y-3" data-testid="intake-results">
          {out.results.map((r, i) => (
            <div key={i} className="bg-white border border-[#2A2625]/10 rounded-xl p-4">
              <p className="text-sm font-medium">{r.file} <span className="text-xs text-[#8C8381]">· {r.kind}</span></p>
              {r.error ? <p className="text-xs text-[#E09494] mt-1">{r.error}</p> :
                <pre className="text-xs bg-[#FCFAF8] rounded-lg p-3 mt-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(r.extracted, null, 2)}</pre>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2A2625]/40 backdrop-blur-sm p-4" data-testid={testid}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[88vh] overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-[#2A2625]/10 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-display font-semibold">{title}</h3>
          <button onClick={onClose} className="text-[#8C8381] hover:text-[#2A2625]"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
